import { Box, BraidPortal, ContentBlock } from 'braid-design-system';
import React, { type HTMLProps, type ReactNode, useState } from 'react';
import { deviceType } from 'react-device-detect';

import { type PreviewLayout, PreviewLayoutEnum } from '../../types';
import { useIsMounted } from '../../utils/useIsMounted';

import { useCallbackWithTimeout } from './useCallbackWithTimeout';
import { useParentStyles } from './useParentStyles';

import * as styles from './index.css';

const IFRAME_CONTENT_WRAPPER_ID = 'iframe-content-wrapper';
const IFRAME_LOAD_DELAY = 200; // magic number to wait before iframe loads
const STYLES_LOAD_DELAY = 200; // based on the longest downloading time of css files under 4G

type Props = {
  children: ReactNode;
  id: string;
  layout: PreviewLayout;
} & HTMLProps<HTMLIFrameElement>;

export const IframePreview = (props: Props) => {
  const { children, id, layout, title, ...restProps } = props;

  const [frame, setFrame] = useState<HTMLIFrameElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isInitialised, setIsInitialised] = useState(false);
  const isMounted = useIsMounted();

  const doc = typeof window !== 'undefined' ? window.document : undefined;

  useParentStyles(doc, frame?.contentDocument);

  const sizeToContent = layout === PreviewLayoutEnum.DESKTOP;
  const inlineStyle =
    sizeToContent && contentHeight ? { height: `${contentHeight}px` } : {};

  const containerClass =
    layout === PreviewLayoutEnum.DESKTOP
      ? styles.desktopContainer
      : styles.mobileContainer;

  const contentWrapperTarget = frame?.contentDocument?.getElementById(
    IFRAME_CONTENT_WRAPPER_ID,
  );

  const onIframeLoad = () => {
    if (doc && isMounted() && frame?.contentDocument) {
      if (!isInitialised) {
        const contentWrapper = doc.createElement('div');
        contentWrapper.id = IFRAME_CONTENT_WRAPPER_ID;
        contentWrapper.style.pointerEvents = 'none';
        contentWrapper.style.opacity = '0';
        frame.contentDocument.body.appendChild(contentWrapper);

        setIsInitialised(true);
      } else {
        const isMobile = deviceType === 'mobile';
        const stylesLoadDelay = isMobile ? STYLES_LOAD_DELAY : 0;

        setTimeout(() => {
          if (contentWrapperTarget) {
            contentWrapperTarget.style.opacity = '1';
          }
        }, stylesLoadDelay);

        const newHeight = frame.contentDocument.body.scrollHeight;

        if (newHeight && newHeight > contentHeight) {
          setContentHeight(newHeight);
        }
      }
    }
  };

  const handleIframeLoad = useCallbackWithTimeout(
    onIframeLoad,
    IFRAME_LOAD_DELAY,
  );

  if (!doc) {
    return null;
  }

  return (
    <Box className={containerClass}>
      <ContentBlock>
        <iframe
          id={id}
          className={`${styles.frame} ${styles[layout]}`}
          title={title}
          onLoad={handleIframeLoad}
          ref={setFrame}
          style={inlineStyle}
          {...restProps}
        >
          {isInitialised && contentWrapperTarget && (
            <BraidPortal container={contentWrapperTarget}>
              {children}
            </BraidPortal>
          )}
        </iframe>
      </ContentBlock>
    </Box>
  );
};
